/**
 * 『物語りのワルツ』 (Story's Waltz)
 * Music / Lyrics / Arrangement: samayuzame
 * MV Director: ぬヴェントス / Illustration: orie / Typography: 本忠学
 * Official MV: https://youtu.be/Kz424oVx9_o
 *
 * ジェネラティブアート・コンセプト（1シーン完結・3拍子の開花とプリズム回路）:
 * - MVの象徴的な視覚モチーフ（アネモネの花、垂れ下がる黄金の蔦、白い花弁と幾何学ワイヤーフレームの渦、
 *   吊り下げられた螺旋モビール・シャンデリア、虹色グラデーションの直角L字リボン回路、シアン×ピンクの網点花雲）を
 *   シーン切り替えなしの1つの完結した空間に統合。
 * - タイポグラフィの過剰な主張を抑え、コンパス円弧や中心軸にそっと刻まれる繊細な詩的トレースとして融合。
 * - 「再生成（Reroll）」ボタンを押すたびに、MVを象徴する4つの色彩世界
 *   （0: 闇の結晶格子と白花のワイヤーフレーム渦, 1: 白昼夢の黄金蔦とアネモネ・網点花雲,
 *     2: ターコイズ×深紅の反転開花, 3: コーラルオレンジ×シアンの二色プリズム）が切り替わり、
 *   L字プリズム回路や花々の配置が新たに生成される。
 * - キャンバスをクリックすると、その場所にアネモネの花と虹色L字リボン回路が新たに咲き広がる。
 */

export class WaltzGenerator {
  constructor(p) {
    this.p = p;
    this.time = 0;
    this.waltzPhase = 0; // 3/4拍子（ワルツ）の位相
    this.worldMode = -1; // 初回initで決定、再生成で4つのMV色彩世界を切り替え

    // 垂れ下がる黄金の蔦・糸
    this.hangingVines = [];
    // 周囲に咲くアネモネの花群
    this.anemoneBlossoms = [];
    // シアン・ピンクの網点（ハーフトーン）花雲
    this.halftoneClouds = [];
    // 直角（90度）に折れ曲がる虹色グラデーションL字リボン回路群
    this.prismaticRibbons = [];
    // 空間を舞う四芒星ダイヤ（✦）と花弁粒子
    this.floatingPetals = [];
    // クリックで咲くインタラクティブな花＆L字リボン波紋
    this.spawnedBlooms = [];
    // カーソル移動軌跡の縫い糸＆四芒星トレイル
    this.cursorTrail = [];

    this.pointerX = 0;
    this.pointerY = 0;
    this.parallaxX = 0;
    this.parallaxY = 0;
    this.waltzBoost = 0;
  }

  onReroll() {
    // 再生成ボタンを押すごとに4つのMV色彩世界を切り替える
    this.worldMode = (this.worldMode + 1) % 4;
    this.waltzBoost = 1.2;
  }

  init(params) {
    const p = this.p;
    const w = p.width || 800;
    const h = p.height || 600;
    const density = params ? params.density : 0.5;
    const scale = Math.min(w, h) / 720;

    if (this.worldMode < 0) {
      this.worldMode = Math.floor(p.random(4));
    }

    this.pointerX = w * 0.5;
    this.pointerY = h * 0.5;
    this.hangingVines = [];
    this.anemoneBlossoms = [];
    this.halftoneClouds = [];
    this.prismaticRibbons = [];
    this.floatingPetals = [];
    this.spawnedBlooms = [];
    this.cursorTrail = [];

    // 1. 天井から垂れ下がる黄金〜オリーブ色の蔦・リボン糸
    const vineCount = Math.floor(12 + density * 12);
    for (let i = 0; i < vineCount; i++) {
      this.hangingVines.push({
        xRatio: p.random(0.04, 0.96),
        lengthRatio: p.random(0.28, 0.88),
        thickness: p.random(1.2, 3.6),
        swaySpeed: p.random(0.7, 1.6),
        swayAmp: p.random(6, 20),
        phase: p.random(p.TWO_PI),
        hueType: i % 3
      });
    }

    // 2. 直角（L字・階段状）に走る虹色グラデーションリボン回路の生成
    const ribbonCount = Math.floor(6 + density * 6);
    for (let i = 0; i < ribbonCount; i++) {
      const rx = p.random(0.12, 0.88) * w;
      const ry = p.random(0.16, 0.84) * h;
      const horizLen = p.random(70, 165) * scale * (p.random() < 0.5 ? 1 : -1);
      const vertLen = p.random(65, 155) * scale * (p.random() < 0.5 ? 1 : -1);
      this.prismaticRibbons.push({
        x: rx,
        y: ry,
        horizLen,
        vertLen,
        thickness: p.random(10, 22) * scale,
        colorOffset: p.random(1.0),
        phase: p.random(p.TWO_PI),
        depth: p.random(0.5, 1.3)
      });
    }

    // 3. シアンとピンクの網点（ハーフトーン）花雲
    const cloudCount = Math.floor(10 + density * 10);
    for (let i = 0; i < cloudCount; i++) {
      const ang = p.random(p.TWO_PI);
      const dist = p.random(0.22, 0.46);
      this.halftoneClouds.push({
        xRatio: 0.5 + Math.cos(ang) * dist,
        yRatio: 0.5 + Math.sin(ang) * dist,
        radius: p.random(20, 46),
        isCyan: i % 2 === 0,
        driftSpeed: p.random(0.3, 0.8),
        phase: p.random(p.TWO_PI)
      });
    }

    // 4. 画面周辺に咲くアネモネの花々（朱赤・ライラック紫・コバルトブルー・ローズ）
    const flowerCount = Math.floor(10 + density * 10);
    const flowerPalette = [
      [232, 76, 32],   // 朱赤アネモネ
      [182, 86, 220],  // ライラック紫
      [68, 118, 235],  // コバルトブルー
      [228, 88, 162]   // ローズピンク
    ];
    for (let i = 0; i < flowerCount; i++) {
      const side = i % 2 === 0 ? p.random(0.06, 0.28) : p.random(0.72, 0.94);
      this.anemoneBlossoms.push({
        xRatio: side,
        yRatio: p.random(0.18, 0.86),
        radius: p.random(16, 32),
        petals: Math.floor(p.random(6, 9)),
        rot: p.random(p.TWO_PI),
        rotSpeed: p.random(-0.3, 0.3),
        col: flowerPalette[i % flowerPalette.length],
        phase: p.random(p.TWO_PI),
        depth: p.random(0.7, 1.4)
      });
    }

    // 5. 空間を舞う四芒星ダイヤ（✦）と花弁粒子
    const petalCount = Math.floor(40 + density * 50);
    for (let i = 0; i < petalCount; i++) {
      this.floatingPetals.push({
        x: p.random(w),
        y: p.random(h),
        vx: p.random(-0.45, 0.45),
        vy: p.random(-0.65, 0.35),
        size: p.random(4, 11),
        rot: p.random(p.TWO_PI),
        rotSpeed: p.random(-0.03, 0.03),
        type: i % 4,
        phase: p.random(p.TWO_PI)
      });
    }
  }

  onPointerDown(x, y) {
    const p = this.p;
    const scale = Math.min(p.width, p.height) / 720;

    this.waltzBoost = 1.4;

    // クリックした場所にアネモネの花＋ワイヤーフレーム波紋＋虹色L字リボンが咲く
    const flowerPalette = [
      [232, 76, 32],
      [42, 208, 232],
      [228, 42, 138],
      [152, 68, 235]
    ];
    this.spawnedBlooms.push({
      x,
      y,
      radius: 8,
      maxRadius: p.random(95, 165) * scale,
      flowerRadius: p.random(24, 42) * scale,
      petals: Math.floor(p.random(6, 9)),
      angle: p.random(p.TWO_PI),
      alpha: 255,
      col: flowerPalette[Math.floor(p.random(flowerPalette.length))],
      horizLen: p.random(55, 110) * scale * (p.random() < 0.5 ? 1 : -1),
      vertLen: p.random(55, 110) * scale * (p.random() < 0.5 ? 1 : -1),
      seed: p.random(1.0)
    });

    if (this.spawnedBlooms.length > 7) {
      this.spawnedBlooms.shift();
    }
  }

  onPointerMove(x, y) {
    const p = this.p;
    this.pointerX = x;
    this.pointerY = y;

    if (p.frameCount % 2 === 0) {
      this.cursorTrail.push({
        x,
        y,
        size: p.random(5, 11),
        hueT: (this.time * 0.35) % 1.0,
        alpha: 210
      });
      if (this.cursorTrail.length > 26) {
        this.cursorTrail.shift();
      }
    }
  }

  draw(params) {
    const p = this.p;
    const w = p.width;
    const h = p.height;
    const motion = params ? params.motion : 0.5;
    const glow = params ? params.glow : 0.6;
    const scale = Math.min(w, h) / 720;

    const dt = Math.min(p.deltaTime || 16.67, 50) / 16.67;
    const speedMult = 0.45 + motion * 1.15;
    this.time += 0.016 * dt * speedMult;

    // 3/4拍子（ワルツ: 1 - 2 - 3）の優美なリズム鼓動
    this.waltzPhase += 0.046 * dt * speedMult;
    const measurePos = (this.waltzPhase % p.TWO_PI) / p.TWO_PI;
    const beat1 = Math.exp(-Math.pow((measurePos - 0.0) * 6.5, 2));
    const beat2 = 0.45 * Math.exp(-Math.pow((measurePos - 0.333) * 8.5, 2));
    const beat3 = 0.45 * Math.exp(-Math.pow((measurePos - 0.666) * 8.5, 2));
    const waltzPulse = beat1 + beat2 + beat3 + this.waltzBoost * 0.4;
    this.waltzBoost = p.lerp(this.waltzBoost, 0, 0.06 * dt);

    // パララックス補間
    const targetPx = ((this.pointerX || w * 0.5) / w - 0.5) * 26;
    const targetPy = ((this.pointerY || h * 0.5) / h - 0.5) * 26;
    this.parallaxX = p.lerp(this.parallaxX, targetPx, 0.07);
    this.parallaxY = p.lerp(this.parallaxY, targetPy, 0.07);

    const cx = w * 0.5 - this.parallaxX * 0.45;
    const cy = h * 0.5 - this.parallaxY * 0.45;

    // =========================================================================
    // 1. 背景と空間パターン（再生成で4つのMV色彩世界を切り替え）
    // =========================================================================
    this.drawWorldBackground(w, h, scale, glow);

    // =========================================================================
    // 2. 天井から垂れ下がる黄金の蔦・糸と、水平の極彩色リボン波
    // =========================================================================
    this.drawHangingVinesAndRibbonWaves(w, h, cy, scale, glow);

    // =========================================================================
    // 3. シアン・ピンクの網点（ハーフトーン）花雲
    // =========================================================================
    this.drawHalftoneFlowerClouds(w, h, scale);

    // =========================================================================
    // 4. 直角（L字・階段状）に走る虹色グラデーションリボン回路
    // =========================================================================
    this.drawPrismaticLRibbonCircuits(w, h, scale, waltzPulse);

    // =========================================================================
    // 5. メイン：白い花弁と幾何学ワイヤーフレーム（有形のトレース）の3拍子渦＋コンパス軌道
    // =========================================================================
    this.drawCentralWireframeFlowerVortex(cx, cy, w, h, scale, waltzPulse, glow);

    // =========================================================================
    // 6. 上部に吊り下げられた「螺旋モビール・シャンデリア」と周辺のアネモネ群
    // =========================================================================
    this.drawSpiralChandelierAndAnemones(cx, w, h, scale, waltzPulse);

    // =========================================================================
    // 7. クリックで咲いたインタラクティブ花＆L字リボン波紋
    // =========================================================================
    this.updateAndDrawSpawnedBlooms(scale, dt, waltzPulse);

    // =========================================================================
    // 8. 空間を舞う四芒星ダイヤ（✦）・花弁粒子・カーソル縫い糸トレイル
    // =========================================================================
    this.updateAndDrawFloatingPetals(w, h, scale, dt);
    this.updateAndDrawCursorTrail(dt);

    // =========================================================================
    // 9. 控えめで繊細な詩的トレースとシネマスコープ上下黒帯
    // =========================================================================
    this.drawSubtlePoeticTraceAndLetterbox(w, h, cx, cy, scale);
  }

  /**
   * 1. 4つのMV色彩世界に応じた背景と幾何学格子の描画
   */
  drawWorldBackground(w, h, scale, glow) {
    const p = this.p;

    if (this.worldMode === 0) {
      // World 0: 闇の結晶格子空間（#1d2226 + ※クロスヘア結晶格子）
      p.background(28, 33, 38);
      const cell = 86 * scale;
      p.stroke(215, 226, 236, 28 + glow * 16);
      p.strokeWeight(1.0 * scale);
      for (let x = cell * 0.5; x < w; x += cell) {
        for (let y = cell * 0.5; y < h; y += cell) {
          const gx = x - this.parallaxX * 0.2;
          const gy = y - this.parallaxY * 0.2;
          const sz = 18 * scale;
          p.line(gx - sz, gy - sz, gx + sz, gy + sz);
          p.line(gx + sz, gy - sz, gx - sz, gy + sz);
        }
      }
    } else if (this.worldMode === 1) {
      // World 1: 白昼夢のウォームホワイト空間（#fcfaf5）
      p.background(252, 250, 245);
    } else if (this.worldMode === 2) {
      // World 2: ターコイズ×深紅の反転世界（#56d1cf）
      p.background(86, 209, 207);
    } else {
      // World 3: コーラルオレンジ×ターコイズシアンの斜め二色分割世界
      p.background(236, 126, 94);
      p.push();
      p.noStroke();
      p.fill(112, 242, 238);
      p.beginShape();
      p.vertex(w * 0.38, 0);
      p.vertex(w, 0);
      p.vertex(w, h);
      p.vertex(w * 0.64, h);
      p.endShape(p.CLOSE);
      p.pop();
    }
  }

  /**
   * 2. 天井から垂れ下がる黄金の蔦・糸と、水平の極彩色リボン波
   */
  drawHangingVinesAndRibbonWaves(w, h, cy, scale, glow) {
    const p = this.p;
    const isDark = this.worldMode === 0;

    // 水平にうねる極彩色リボン波（闇モードでは鮮やかに発光、明モードでは淡い水彩リボン）
    p.push();
    p.noFill();
    for (let r = 0; r < 4; r++) {
      const col = this.samplePrismaticGradient((r * 0.25 + this.time * 0.08) % 1.0);
      p.stroke(col[0], col[1], col[2], isDark ? 155 : 75);
      p.strokeWeight((2.0 + r * 1.2) * scale);
      p.beginShape();
      for (let x = 0; x <= w; x += 20) {
        const nx = x / w;
        const env = Math.exp(-Math.pow((nx - 0.5) * 3.8, 2));
        const wave = Math.sin(nx * 12 - this.time * 2.6 + r * 0.7) * 28 * scale * env;
        p.vertex(x, cy + (r - 1.5) * 12 * scale + wave);
      }
      p.endShape();
    }

    // 垂れ下がる黄金〜オリーブ色の蔦・糸
    for (let i = 0; i < this.hangingVines.length; i++) {
      const v = this.hangingVines[i];
      const baseX = v.xRatio * w - this.parallaxX * 0.35;
      const maxY = v.lengthRatio * h;

      if (this.worldMode === 2) {
        p.stroke(196, 18, 32, 135);
      } else if (v.hueType === 0) {
        p.stroke(212, 186, 82, isDark ? 115 : 175);
      } else if (v.hueType === 1) {
        p.stroke(232, 206, 118, isDark ? 95 : 145);
      } else {
        p.stroke(168, 178, 64, isDark ? 110 : 165);
      }
      p.strokeWeight(v.thickness * scale);

      p.beginShape();
      for (let y = 0; y <= maxY; y += 20) {
        const ratio = y / h;
        const sway = Math.sin(ratio * 4.5 + this.time * v.swaySpeed + v.phase) * v.swayAmp * scale * ratio;
        p.vertex(baseX + sway, y);
      }
      p.endShape();
    }
    p.pop();
  }

  /**
   * 3. シアンとピンクの網点（ハーフトーン）花雲の描画
   */
  drawHalftoneFlowerClouds(w, h, scale) {
    const p = this.p;
    p.push();
    p.noStroke();
    for (let i = 0; i < this.halftoneClouds.length; i++) {
      const c = this.halftoneClouds[i];
      const cx = c.xRatio * w + Math.sin(this.time * c.driftSpeed + c.phase) * 12 * scale - this.parallaxX * 0.5;
      const cy = c.yRatio * h + Math.cos(this.time * c.driftSpeed + c.phase) * 10 * scale - this.parallaxY * 0.5;
      const rad = c.radius * scale;

      p.fill(c.isCyan ? [68, 224, 238, 115] : [228, 86, 142, 115]);
      for (let k = 0; k < 4; k++) {
        const a = (p.TWO_PI / 4) * k + 0.4;
        p.circle(cx + Math.cos(a) * rad * 0.34, cy + Math.sin(a) * rad * 0.34, rad * 0.8);
      }

      // 網点ドットパターン
      const step = 6.0 * scale;
      p.fill(c.isCyan ? [18, 175, 198, 195] : [195, 42, 98, 195]);
      for (let dx = -rad * 0.65; dx <= rad * 0.65; dx += step) {
        for (let dy = -rad * 0.65; dy <= rad * 0.65; dy += step) {
          if (dx * dx + dy * dy < rad * rad * 0.44) {
            p.circle(cx + dx, cy + dy, 2.2 * scale);
          }
        }
      }
    }
    p.pop();
  }

  /**
   * 4. 直角（L字・階段状）に折れ曲がる虹色グラデーションリボン回路の描画
   * （文字で画面を埋め尽くす代わりに、本忠学×ぬヴェントスのL字グラデーション枠を抽象幾何学アートとして展開）
   */
  drawPrismaticLRibbonCircuits(w, h, scale, waltzPulse) {
    const p = this.p;
    p.push();

    for (let i = 0; i < this.prismaticRibbons.length; i++) {
      const rb = this.prismaticRibbons[i];
      const rx = rb.x - this.parallaxX * rb.depth;
      const ry = rb.y - this.parallaxY * rb.depth;
      const breath = 0.85 + 0.15 * Math.sin(this.time * 1.6 + rb.phase) + waltzPulse * 0.08;

      const hLen = rb.horizLen * breath;
      const vLen = rb.vertLen * breath;
      const thick = rb.thickness;

      // 横方向のグラデーションバー
      const hx = hLen >= 0 ? rx : rx + hLen;
      this.drawGradientStrip(hx, ry - thick * 0.5, Math.abs(hLen), thick, false, rb.colorOffset);

      // コーナーから直角（90度）に折れ曲がる縦方向のグラデーションバー
      const cornerX = rx + hLen;
      const vy = vLen >= 0 ? ry : ry + vLen;
      this.drawGradientStrip(cornerX - thick * 0.5, vy, thick, Math.abs(vLen), true, rb.colorOffset + 0.35);

      // リボン回路を繋ぐ細い直角ガイド線
      const lineCol = this.samplePrismaticGradient((rb.colorOffset + this.time * 0.15) % 1.0);
      p.noFill();
      p.stroke(lineCol[0], lineCol[1], lineCol[2], 185);
      p.strokeWeight(1.8 * scale);
      p.line(rx, ry, cornerX, ry);
      p.line(cornerX, ry, cornerX, ry + vLen);
    }

    p.pop();
  }

  /**
   * 虹色グラデーション（ゴールド→コーラル→マゼンタ→バイオレット→シアン）のストリップ描画
   */
  drawGradientStrip(x, y, w, h, isVertical, colorOffset) {
    const p = this.p;
    const steps = 18;
    p.noStroke();

    // 立体オフセットシャドウ枠
    p.fill(245, 200, 48, 130);
    p.rect(x - 2, y - 2, w + 4, h + 4);

    for (let i = 0; i < steps; i++) {
      const t = ((i / steps) + colorOffset + this.time * 0.12) % 1.0;
      const col = this.samplePrismaticGradient(t);
      p.fill(col[0], col[1], col[2], 215);
      if (isVertical) {
        p.rect(x, y + (h / steps) * i, w, h / steps + 1.2);
      } else {
        p.rect(x + (w / steps) * i, y, w / steps + 1.2, h);
      }
    }
  }

  /**
   * プリズムグラデーション補間
   */
  samplePrismaticGradient(t) {
    const stops = [
      [245, 208, 42],  // イエローゴールド
      [242, 108, 72],  // コーラルオレンジ
      [228, 28, 138],  // マゼンタ
      [138, 42, 225],  // バイオレット
      [38, 205, 232]   // ターコイズシアン
    ];
    const scaled = (t % 1.0) * (stops.length - 1);
    const idx = Math.floor(scaled);
    const frac = scaled - idx;
    const c1 = stops[idx];
    const c2 = stops[(idx + 1) % stops.length];
    return [
      c1[0] + (c2[0] - c1[0]) * frac,
      c1[1] + (c2[1] - c1[1]) * frac,
      c1[2] + (c2[2] - c1[2]) * frac
    ];
  }

  /**
   * 5. 中央の主役：「白い花弁と幾何学ワイヤーフレーム（有形のトレース）が重なり3拍子で回る花の渦」＋コンパス円弧
   */
  drawCentralWireframeFlowerVortex(cx, cy, w, h, scale, waltzPulse, glow) {
    const p = this.p;
    const isDark = this.worldMode === 0;
    const isTurquoise = this.worldMode === 2;

    p.push();
    p.translate(cx, cy);

    // 1. 外周を巡るコンパス円弧軌道と四芒星（✦）（waltz_maxres3.jpg の象徴的黒／白リング）
    const ringCol = isDark ? [225, 236, 248, 175] : isTurquoise ? [196, 16, 28, 210] : [20, 22, 28, 215];
    p.noFill();
    p.stroke(ringCol[0], ringCol[1], ringCol[2], ringCol[3]);
    p.strokeWeight(2.0 * scale);
    const outerR = (215 + waltzPulse * 8) * scale;
    p.circle(0, 0, outerR * 2);

    p.strokeWeight(1.2 * scale);
    p.arc(0, 0, outerR * 2.32, outerR * 2.32, this.time * 0.25, this.time * 0.25 + p.PI * 1.35);
    p.arc(0, 0, outerR * 1.72, outerR * 1.72, -this.time * 0.3, -this.time * 0.3 + p.PI * 1.2);

    // コンパス軌道上の四芒星ダイヤ（✦）
    for (let s = 0; s < 8; s++) {
      const a = (p.TWO_PI / 8) * s + this.time * 0.18;
      const r = outerR * (s % 2 === 0 ? 1.0 : 1.16);
      this.drawFourPointStar(Math.cos(a) * r, Math.sin(a) * r, 9 * scale, ringCol);
    }

    // 2. 放射状の幾何学ワイヤーフレーム線（有形のトレース / データの世界）
    p.stroke(ringCol[0], ringCol[1], ringCol[2], 65);
    p.strokeWeight(0.9 * scale);
    for (let k = 0; k < 16; k++) {
      const a = (p.TWO_PI / 16) * k - this.time * 0.1;
      p.line(0, 0, Math.cos(a) * outerR * 1.15, Math.sin(a) * outerR * 1.15);
    }

    // 3. 幾重にも重なって回転する半透明の花弁渦（waltz_maxres2.jpg のバラ／アネモネ渦）
    const rings = 5;
    for (let r = rings; r >= 1; r--) {
      const ringRadius = (r * 34 + waltzPulse * 7) * scale;
      const petalCount = 6 + r * 2;
      const dir = r % 2 === 0 ? 1 : -1;
      const baseRot = this.time * 0.24 * dir + r * 0.4;

      for (let k = 0; k < petalCount; k++) {
        const a = baseRot + (p.TWO_PI / petalCount) * k;
        p.push();
        p.rotate(a);
        p.translate(ringRadius, 0);
        p.rotate(0.42 * dir);

        if (isTurquoise) {
          // ターコイズモードでは深紅と白の花弁渦
          p.fill(r % 2 === 0 ? [198, 16, 28, 165] : [245, 250, 252, 175]);
          p.stroke(198, 16, 28, 210);
        } else if (isDark) {
          // 闇モードでは幻想的なアイスブルー〜純白の半透明花弁
          const lum = p.map(r, 1, rings, 240, 155);
          p.fill(lum - 20, lum - 5, lum + 15, 130 + (rings - r) * 18);
          p.stroke(235, 245, 255, 145);
        } else {
          // 白昼夢・コーラルモードでは純白〜淡いプリズム色の花弁と黒／シアンのワイヤーフレーム
          const col = this.samplePrismaticGradient(((k / petalCount) + r * 0.15) % 1.0);
          p.fill(r === 1 ? col[0] : 252, r === 1 ? col[1] : 248, r === 1 ? col[2] : 252, r === 1 ? 185 : 165);
          p.stroke(32, 38, 46, 135);
        }
        p.strokeWeight(1.1 * scale);

        const pw = (24 + r * 12) * scale;
        const ph = (42 + r * 18) * scale;
        p.beginShape();
        p.vertex(0, -ph * 0.5);
        p.bezierVertex(pw * 0.8, -ph * 0.3, pw * 0.8, ph * 0.3, 0, ph * 0.5);
        p.bezierVertex(-pw * 0.4, ph * 0.2, -pw * 0.4, -ph * 0.2, 0, -ph * 0.5);
        p.endShape(p.CLOSE);
        p.pop();
      }
    }

    // 4. 中心で輝く双葉勾玉渦（イントロの69モチーフ）とアネモネの黒芯
    p.noStroke();
    p.fill(250, 248, 252, 240);
    p.circle(0, 0, 32 * scale);
    p.fill(18, 16, 22, 250);
    p.circle(0, 0, 18 * scale);

    p.pop();
  }

  /**
   * 6. 上部に吊り下げられた「螺旋モビール・シャンデリア」と周辺に咲くアネモネの花々
   */
  drawSpiralChandelierAndAnemones(cx, w, h, scale, waltzPulse) {
    const p = this.p;
    const isDark = this.worldMode === 0;
    const lineCol = isDark ? [220, 230, 240, 170] : [26, 30, 36, 185];

    // 上部中央の螺旋モビール・シャンデリア（waltz_tile_interlude1.png / waltz_tile_final.png）
    p.push();
    p.translate(cx, h * 0.14 - this.parallaxY * 0.3);
    p.noFill();
    p.stroke(lineCol[0], lineCol[1], lineCol[2], lineCol[3]);
    for (let r = 1; r <= 4; r++) {
      p.strokeWeight((r % 2 === 0 ? 1.8 : 1.0) * scale);
      const ox = Math.sin(this.time * 1.3 + r) * 8 * scale;
      p.ellipse(ox, r * 8 * scale, (220 - r * 32) * scale, (52 - r * 6) * scale);
    }
    p.pop();

    // 周辺に咲くアネモネの花々
    for (let i = 0; i < this.anemoneBlossoms.length; i++) {
      const f = this.anemoneBlossoms[i];
      const fx = f.xRatio * w - this.parallaxX * f.depth;
      const fy = f.yRatio * h - this.parallaxY * f.depth;
      const rot = f.rot + this.time * f.rotSpeed;
      const rad = (f.radius + waltzPulse * 3.5) * scale;
      this.drawSingleAnemone(fx, fy, rad, f.petals, rot, f.col, 0.92);
    }
  }

  /**
   * アネモネの花1輪の描画（鮮やかな花弁＋白い中心リング＋黒い芯）
   */
  drawSingleAnemone(x, y, radius, petals, rot, rgb, fade) {
    const p = this.p;
    p.push();
    p.translate(x, y);
    p.rotate(rot);
    p.noStroke();

    for (let i = 0; i < petals; i++) {
      const a = (p.TWO_PI / petals) * i;
      p.push();
      p.rotate(a);
      p.fill(rgb[0], rgb[1], rgb[2], 215 * fade);
      p.ellipse(radius * 0.52, 0, radius * 0.95, radius * 0.68);
      p.pop();
    }

    p.fill(250, 248, 252, 240 * fade);
    p.circle(0, 0, radius * 0.58);
    p.fill(18, 16, 22, 248 * fade);
    p.circle(0, 0, radius * 0.36);
    p.pop();
  }

  /**
   * 7. クリックで咲くインタラクティブ花＆虹色L字リボンの更新・描画
   */
  updateAndDrawSpawnedBlooms(scale, dt, waltzPulse) {
    const p = this.p;
    for (let i = this.spawnedBlooms.length - 1; i >= 0; i--) {
      const sb = this.spawnedBlooms[i];
      sb.radius = p.lerp(sb.radius, sb.maxRadius, 0.09 * dt);
      sb.angle += 0.025 * dt;
      sb.alpha -= 2.4 * dt;

      if (sb.alpha <= 0) {
        this.spawnedBlooms.splice(i, 1);
        continue;
      }

      const fade = sb.alpha / 255;

      // 直角に伸びる虹色L字リボン
      const hx = sb.horizLen >= 0 ? sb.x : sb.x + sb.horizLen;
      this.drawGradientStrip(hx, sb.y - 6 * scale, Math.abs(sb.horizLen) * fade, 12 * scale, false, sb.seed);
      const vy = sb.vertLen >= 0 ? sb.y : sb.y + sb.vertLen;
      this.drawGradientStrip(sb.x + sb.horizLen - 6 * scale, vy, 12 * scale, Math.abs(sb.vertLen) * fade, true, sb.seed + 0.3);

      // ワイヤーフレーム波紋リングと開花するアネモネ
      p.push();
      p.translate(sb.x, sb.y);
      p.rotate(sb.angle);
      p.noFill();
      p.stroke(sb.col[0], sb.col[1], sb.col[2], sb.alpha * 0.8);
      p.strokeWeight(1.6 * scale);
      p.circle(0, 0, sb.radius * 1.6);
      p.pop();

      this.drawSingleAnemone(sb.x, sb.y, sb.flowerRadius * (0.6 + 0.4 * fade), sb.petals, sb.angle, sb.col, fade);
    }
  }

  /**
   * 8. 空間を舞う四芒星ダイヤ（✦）と花弁粒子の更新・描画
   */
  updateAndDrawFloatingPetals(w, h, scale, dt) {
    const p = this.p;
    for (let i = 0; i < this.floatingPetals.length; i++) {
      const fp = this.floatingPetals[i];
      fp.x += (fp.vx + Math.sin(this.time + fp.phase) * 0.35) * dt;
      fp.y += fp.vy * dt;
      fp.rot += fp.rotSpeed * dt;

      if (fp.x < -20) fp.x = w + 20;
      if (fp.x > w + 20) fp.x = -20;
      if (fp.y < -20) fp.y = h + 20;
      if (fp.y > h + 20) fp.y = -20;

      if (fp.type === 0) {
        const starCol = this.worldMode === 0 ? [230, 240, 250, 200] : [20, 22, 28, 205];
        this.drawFourPointStar(fp.x, fp.y, fp.size * scale, starCol);
      } else {
        p.push();
        p.translate(fp.x, fp.y);
        p.rotate(fp.rot);
        p.noStroke();
        if (fp.type === 1) p.fill(42, 208, 228, 185);
        else if (fp.type === 2) p.fill(228, 48, 138, 185);
        else p.fill(232, 76, 32, 185);
        p.ellipse(0, 0, fp.size * 1.3 * scale, fp.size * 0.65 * scale);
        p.pop();
      }
    }
  }

  /**
   * 四芒星ダイヤ（✦）の描画ヘルパー
   */
  drawFourPointStar(x, y, radius, rgba) {
    const p = this.p;
    p.push();
    p.translate(x, y);
    p.noStroke();
    p.fill(rgba[0], rgba[1], rgba[2], rgba[3]);
    p.beginShape();
    p.vertex(0, -radius);
    p.quadraticVertex(0, 0, radius, 0);
    p.quadraticVertex(0, 0, 0, radius);
    p.quadraticVertex(0, 0, -radius, 0);
    p.quadraticVertex(0, 0, 0, -radius);
    p.endShape(p.CLOSE);
    p.pop();
  }

  /**
   * カーソル移動軌跡の縫い糸＆四芒星トレイル描画
   */
  updateAndDrawCursorTrail(dt) {
    const p = this.p;
    if (this.cursorTrail.length === 0) return;

    p.push();
    p.noFill();
    p.strokeWeight(1.3);
    for (let i = 1; i < this.cursorTrail.length; i++) {
      const prev = this.cursorTrail[i - 1];
      const curr = this.cursorTrail[i];
      const col = this.samplePrismaticGradient(curr.hueT);
      p.stroke(col[0], col[1], col[2], curr.alpha * 0.65);
      p.line(prev.x, prev.y, curr.x, curr.y);
    }

    for (let i = this.cursorTrail.length - 1; i >= 0; i--) {
      const tr = this.cursorTrail[i];
      tr.alpha -= 6.5 * dt;
      if (tr.alpha <= 0) {
        this.cursorTrail.splice(i, 1);
        continue;
      }
      const col = this.samplePrismaticGradient(tr.hueT);
      this.drawFourPointStar(tr.x, tr.y, tr.size * 0.65, [col[0], col[1], col[2], tr.alpha]);
    }
    p.pop();
  }

  /**
   * 9. シネマスコープ上下黒帯（レターボックス）の描画
   */
  drawSubtlePoeticTraceAndLetterbox(w, h, cx, cy, scale) {
    const p = this.p;

    // MV特有のシネマスコープ上下黒帯（レターボックス）
    const barH = Math.round(h * 0.075);
    p.push();
    p.noStroke();
    p.fill(6, 7, 9, 255);
    p.rect(0, 0, w, barH);
    p.rect(0, h - barH, w, barH);
    p.pop();
  }
}
